export interface CloudStorageData {
  url: string;
}

export interface FileStorageData extends CloudStorageData {
  name?: string; // File name in storage, e.g., 'report.pdf'
  type?: string; // MIME type, e.g., 'application/pdf'
  size?: number; // File size in bytes
  metadata?: any;
}

export interface ImgStorageData extends FileStorageData {
  resolution?: string;
  resolutions?: { [key: string]: string };
}

export interface ImgGeneratedStorageData extends ImgStorageData {
  model?: string;
  prompt?: string;
}

// Use as input for uploadImage
export interface StorageImageSettings {
  path?: string;
  fileName?: string;
  cropSettings?: ImageCropSettings;
}

export interface ImageCropSettings {
  resizeToWidth?: number;
  resolutions: Array<number>; // 400, 800, 1200, 1600
  aspectRatio: AspectType;
}

export interface CropImageSettings {
  // Resolutions should be here.
  path: string;
  fileName?: string;
  resizeToWidth?: number;
}

export interface ImageMultipleCrops {
  file: File;
  defaultImageBlob?: Blob;
  imagesBlobs: { [resolution: number]: Blob };
  settings: { renameFile: string; width?: number };
}

export const AspectRatioOptions: AspectRatioOption[] = [
  { value: '1:1', label: 'square', description: 'Square (1:1)', valueRatio: 1 / 1 },
  { value: '1:2', label: 'vertical_1_2', description: 'Vertical (1:2)', valueRatio: 1 / 2 },
  { value: '2:3', label: 'vertical_2_3', description: 'Vertical (2:3)', valueRatio: 2 / 3 },
  { value: '3:4', label: 'vertical_3_4', description: 'Vertical (3:4)', valueRatio: 3 / 4 },
  { value: '4:5', label: 'vertical_4_5', description: 'Vertical (4:5)', valueRatio: 4 / 5 },
  { value: '9:16', label: 'vertical_9_16', description: 'Vertical (9:16)', valueRatio: 9 / 16 },
  { value: '2:1', label: 'horizontal_2_1', description: 'Horizontal (2:1)', valueRatio: 2 / 1 },
  { value: '3:2', label: 'horizontal_3_2', description: 'Horizontal (3:2)', valueRatio: 3 / 2 },
  { value: '4:3', label: 'horizontal_4_3', description: 'Horizontal (4:3)', valueRatio: 4 / 3 },
  { value: '5:4', label: 'horizontal_5_4', description: 'Horizontal (5:4)', valueRatio: 5 / 4 },
  { value: '16:9', label: 'horizontal_16_9', description: 'Horizontal (16:9)', valueRatio: 16 / 9 },
];

export interface AspectRatioOption {
  label: string;
  description: string;
  value: string;
  valueRatio: number;
}

export enum AspectType {
  Square = 'square',
  Rectangle = 'rectangle',
  Banner = 'banner',
  RectangleLarge = 'rectangleLarge',
  Vertical_9_16 = 'vertical_9_16',
  Vertical_3_5 = 'vertical_3_5',
  Vertical_2_3 = 'vertical_2_3',
}

// Creo que seria bueno refactorizar para solo enviar aspect ratio asi
export enum AspectRatio2 {
  // Vertical
  '9:16' = 16 / 9,
  '3:5' = 3 / 5,
  '5:8' = 5 / 8,
  '2:3' = 2 / 3,
  '1:1' = 1 / 1,
}

export enum ResolutionType {
  Small = 200,
  Medium = 400,
  MediumLarge = 800,
  Large = 1200,
  VeryLarge = 1600,
}

export const AspectRatio = {
  [AspectType.Square]: 1 / 1,
  [AspectType.Rectangle]: 16 / 9,
  [AspectType.Vertical_9_16]: 9 / 16,
  [AspectType.RectangleLarge]: 16 / 8,
  [AspectType.Banner]: 16 / 7,
  [AspectType.Vertical_3_5]: 3 / 5,
  [AspectType.Vertical_2_3]: 2 / 3,
};

export const DEFAULT_SETTINGS: StorageImageSettings = {
  path: '/default-collection/id/please-change-this-subcollection',
  fileName: 'image',
  cropSettings: {
    aspectRatio: AspectType.Square,
    resolutions: [ResolutionType.Small, ResolutionType.MediumLarge],
    resizeToWidth: 450,
  },
};

const FIREBASE_STORAGE_URL_REGEX = /https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?]+)/;

/**
 * Extracts the bucket name from a Firebase Storage URL.
 * The URL is expected to follow the pattern:
 * 'https://firebasestorage.googleapis.com/v0/b/&lt;bucket&gt;/&lt;path&gt;?&lt;query_params&gt;'
 *
 * @param data The CloudStorageData object containing the 'url' property.
 * @returns The bucket name (e.g., 'appingles-pro.appspot.com') or undefined if not found or URL is invalid.
 */
export function extractBucket(data: CloudStorageData): string | undefined {
  if (!data.url) {
    return undefined;
  }
  const match = data.url.match(FIREBASE_STORAGE_URL_REGEX);
  // match[0] is the full matched string
  // match[1] is the first capturing group (bucket)
  // match[2] is the second capturing group (path)
  return match?.[1];
}

/**
 * Extracts the file path from a Firebase Storage URL.
 * The URL is expected to follow the pattern:
 * 'https://firebasestorage.googleapis.com/v0/b/&lt;bucket&gt;/&lt;path&gt;?&lt;query_params&gt;'
 * The extracted path is the part after the bucket and before any query parameters.
 *
 * @param data The CloudStorageData object containing the 'url' property.
 * @returns The file path (e.g., 'scenarios/666506c3b9b5443f4bfab5e0/images/hairdresser.webp')
 *          or undefined if not found or URL is invalid.
 *          Note: This function does not perform URL decoding on the path. If URL-decoded paths
 *          (e.g., converting %2F to /) are needed, `decodeURIComponent` should be applied to the result.
 */
export function extractPath(data: CloudStorageData): string | undefined {
  if (data && data?.url) {
    const match = data.url.match(FIREBASE_STORAGE_URL_REGEX);
    // match[0] is the full matched string
    // match[1] is the first capturing group (bucket)
    // match[2] is the second capturing group (path after 'o/')
    if (match && match[2]) {
      try {
        // Decode URI components, e.g., %2F to /
        return decodeURIComponent(match[2]);
      } catch (e) {
        // Log error if decoding fails.
        // Returning the raw path component as a fallback.
        // Consider if undefined or throwing an error would be more appropriate for your use case.
        console.error(`Failed to decode URI component for path: '${match[2]}'`, e);
        return match[2]; // Fallback to raw path component
      }
    }
    return undefined; // URL did not match expected pattern or path component missing
  } else {
    return undefined;
  }
}
